import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, FolderPlus, Loader2, Bookmark, Star, ShoppingBag, Camera, Sparkles, MessageSquare, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const TABS = [
  { id: "all",            label: "All saved",   icon: Bookmark },
  { id: "atelier",        label: "Atelier",     icon: ShoppingBag },
  { id: "tryon",          label: "Try-ons",     icon: Camera },
  { id: "outfit",         label: "Outfits",     icon: Sparkles },
  { id: "community_post", label: "Social",   icon: Star },
  { id: "stylist",        label: "Stylist",     icon: MessageSquare },
  { id: "collections",    label: "Lookbooks",   icon: FolderPlus },
];

// Item types that make sense to publish to the public Social feed.
const SOCIAL_PUBLISHABLE = new Set(["tryon", "outfit", "atelier"]);

export default function Collections() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [cols, setCols] = useState([]);
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [activeCol, setActiveCol] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: saved }, { data: c }, { data: o }] = await Promise.all([
        api.get("/saved"),
        api.get("/collections"),
        api.get("/outfits"),
      ]);
      setItems(saved);
      setCols(c);
      setOutfits(o);
      if (!activeCol && c[0]) setActiveCol(c[0]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => {
    const m = { all: items.length, collections: cols.length };
    items.forEach((i) => { m[i.item_type] = (m[i.item_type] || 0) + 1; });
    return m;
  }, [items, cols]);

  const visible = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "collections") return [];
    return items.filter((i) => i.item_type === tab);
  }, [items, tab]);

  const removeSaved = async (it) => {
    try {
      await api.delete(`/saved/${it.item_type}/${it.item_id}`);
      setItems((arr) => arr.filter((x) => !(x.item_type === it.item_type && x.item_id === it.item_id)));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const publishToSocial = async (it) => {
    const caption = window.prompt("Caption for your Social post?", "");
    if (caption === null) return; // user cancelled
    try {
      await api.post(
        `/saved/${it.item_type}/${it.item_id}/publish`,
        { caption: caption.trim() || undefined },
      );
      toast.success("Posted to Social");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post to Social");
    }
  };

  const createCollection = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/collections", { name: newName.trim() });
      setCols((c) => [...c, { ...data, count: 0 }]);
      setNewName("");
      toast.success("Collection created");
    } finally { setCreating(false); }
  };

  const removeCollection = async (cid) => {
    if (!confirm("Remove collection?")) return;
    await api.delete(`/collections/${cid}`);
    setCols((c) => c.filter((x) => x.id !== cid));
    if (activeCol?.id === cid) setActiveCol(null);
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="animate-spin" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Curation · Everything you&apos;ve saved</p>
          <h1 className="editorial-h1 mt-3">Saved.</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">
            Bookmark anything — try-ons, outfits, atelier picks, community looks, stylist tips —
            and it lands here for later.
          </p>
        </div>
        {tab === "collections" && (
          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="collection-new-btn" className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                <FolderPlus size={14} className="mr-2" /> New lookbook
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Name your lookbook</DialogTitle></DialogHeader>
              <Input data-testid="collection-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Weekend looks" className="rounded-none h-11" />
              <Button data-testid="collection-save-btn" onClick={createCollection} disabled={creating || !newName.trim()} className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                {creating ? "Creating…" : "Create"}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto pb-1" data-testid="saved-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const n = counts[t.id] || 0;
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`saved-tab-${t.id}`}
              className={`flex items-center gap-2 px-3 h-10 border whitespace-nowrap text-[11px] uppercase tracking-[0.18em] transition-colors ${
                on ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
              }`}
            >
              <Icon size={13} />
              {t.label}
              {n > 0 && (
                <span className={`text-[10px] px-1.5 ${on ? "bg-background/20" : "bg-muted"}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab !== "collections" && (
        <>
          {visible.length === 0 ? (
            <div className="mt-12 border border-dashed border-border p-16 text-center">
              <Bookmark size={28} className="mx-auto text-muted-foreground" />
              <p className="font-serif text-3xl mt-4">Nothing saved yet.</p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Tap the bookmark icon on any try-on, outfit, atelier pick or community look — and it&apos;ll land here.
              </p>
              <div className="mt-6 flex gap-2 justify-center flex-wrap">
                <Link to="/atelier"><Button variant="outline" className="rounded-none uppercase tracking-[0.2em] text-xs h-10">Browse Atelier</Button></Link>
                <Link to="/outfit"><Button variant="outline" className="rounded-none uppercase tracking-[0.2em] text-xs h-10">Generate outfit</Button></Link>
                <Link to="/community"><Button variant="outline" className="rounded-none uppercase tracking-[0.2em] text-xs h-10">Social</Button></Link>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {visible.map((it, i) => (
                <SavedCard
                  key={`${it.item_type}-${it.item_id}-${i}`}
                  it={it}
                  onRemove={() => removeSaved(it)}
                  onPublish={publishToSocial}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === "collections" && (
        <CollectionsView
          cols={cols} outfits={outfits}
          active={activeCol} setActive={setActiveCol}
          onRemoveCol={removeCollection}
        />
      )}
    </div>
  );
}

function SavedCard({ it, onRemove, onPublish }) {
  const s = it.snapshot || {};
  const image = s.hero_image || s.image || s.result_path
    ? (s.hero_image || s.image || fileUrl(s.result_path))
    : null;
  const titleByType = {
    atelier: s.title,
    tryon: `Try-on · ${s.background || "studio"}`,
    outfit: `${s.occasion || "Outfit"} look`,
    community_post: s.caption?.slice(0, 60) || "Community post",
    stylist: s.preview?.slice(0, 60) || "Stylist tip",
    event_outfit: s.event_title || "Event outfit",
  };
  const subByType = {
    atelier: `${s.retailer || ""}${s.price ? ` · ₹${Number(s.price).toLocaleString("en-IN")}` : ""}`,
    tryon: "Saved try-on",
    outfit: `Style ${s.style_score ?? "—"}/10`,
    community_post: `@${s.username || "community"}`,
    stylist: "Stylist note",
    event_outfit: s.event_date || "",
  };
  const linkByType = {
    atelier: "/atelier",
    tryon: "/tryon",
    outfit: "/outfit",
    community_post: "/community",
    stylist: "/stylist",
    event_outfit: "/calendar",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="group relative border border-border bg-card hover:border-foreground transition-colors"
      data-testid={`saved-card-${it.item_type}-${it.item_id}`}
    >
      <div className="aspect-[4/5] bg-muted overflow-hidden relative">
        {image ? (
          <img src={image} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Bookmark className="text-muted-foreground" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 bg-background/85 backdrop-blur text-[9px] uppercase tracking-[0.2em]">
          {it.item_type.replace("_", " ")}
        </span>
        <button
          onClick={onRemove}
          aria-label="Remove"
          data-testid={`saved-remove-${it.item_type}-${it.item_id}`}
          className="absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center bg-background/85 backdrop-blur opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition"
        >
          <Trash2 size={12} />
        </button>
        {SOCIAL_PUBLISHABLE.has(it.item_type) && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onPublish?.(it); }}
            aria-label="Post to Social"
            title="Post to Social feed"
            data-testid={`saved-publish-${it.item_type}-${it.item_id}`}
            className="absolute top-2 right-11 h-8 px-2 inline-flex items-center gap-1 bg-background/85 backdrop-blur opacity-0 group-hover:opacity-100 hover:bg-accent hover:text-accent-foreground transition text-[10px] uppercase tracking-[0.18em]"
          >
            <Share2 size={11} /> Post
          </button>
        )}
      </div>
      <Link to={linkByType[it.item_type] || "/home"} className="block p-3">
        <p className="text-sm font-serif leading-tight line-clamp-2">{titleByType[it.item_type] || it.item_type}</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1.5">
          {subByType[it.item_type] || ""}
        </p>
      </Link>
    </motion.div>
  );
}

function CollectionsView({ cols, outfits, active, setActive, onRemoveCol }) {
  const outfitMap = Object.fromEntries(outfits.map((o) => [o.id, o]));
  const activeOutfits = (active?.outfit_ids || []).map((id) => outfitMap[id]).filter(Boolean);

  return (
    <div className="mt-8 grid lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3">
        {cols.map((c) => (
          <motion.button
            key={c.id}
            data-testid={`collection-${c.id}`}
            onClick={() => setActive(c)}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`text-left border p-4 transition group ${
              active?.id === c.id ? "border-foreground" : "border-border hover:border-foreground"
            }`}
          >
            <div className="aspect-[5/3] overflow-hidden bg-muted mb-3">
              {c.cover_image_path ? (
                <img src={fileUrl(c.cover_image_path)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground uppercase tracking-[0.2em]">empty</div>
              )}
            </div>
            <div className="flex items-baseline justify-between">
              <p className="font-serif text-xl tracking-tight">{c.name}</p>
              {!c.is_default && (
                <span onClick={(e) => { e.stopPropagation(); onRemoveCol(c.id); }} className="opacity-50 hover:opacity-100 cursor-pointer">
                  <Trash2 size={12} />
                </span>
              )}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{c.count} outfit{c.count === 1 ? "" : "s"}</p>
          </motion.button>
        ))}
        {cols.length === 0 && (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No lookbooks yet — create one above.
          </div>
        )}
      </aside>

      <div className="lg:col-span-8">
        {active ? (
          <div>
            <p className="eyebrow">{active.is_default ? "Default · " : "Custom · "}{active.count} outfits</p>
            <h2 className="editorial-h2 mt-2">{active.name}</h2>
            {activeOutfits.length === 0 ? (
              <div className="mt-8 border border-dashed border-border p-16 text-center">
                <p className="font-serif text-2xl">Nothing in here yet.</p>
                <p className="text-sm text-muted-foreground mt-2">Generate an outfit, then save it from the Outfit Generator.</p>
              </div>
            ) : (
              <div className="mt-8 grid md:grid-cols-2 gap-4">
                {activeOutfits.map((o) => (
                  <div key={o.id} className="border border-border bg-card">
                    <div className="grid grid-cols-3 gap-1 p-1">
                      {[o.top, o.bottom, o.shoes].map((it, i) => (
                        <div key={i} className="aspect-square bg-muted overflow-hidden">
                          {it && <img src={fileUrl(it.image_path)} alt="" className="h-full w-full object-cover" />}
                        </div>
                      ))}
                    </div>
                    <p className="p-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">{o.occasion}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="font-serif text-3xl">Pick a lookbook to view it.</p>
          </div>
        )}
      </div>
    </div>
  );
}
